//#region IMPORTS
import { InstoreOrderLineCustomField } from "../../models/instoreOrder";
import { useForm, useFieldArray } from "react-hook-form";
import { useEffect } from "react";
import classNames from "../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  // Field list (name + current value) to edit.
  fields: InstoreOrderLineCustomField[];
  onSubmitHandler: (values: InstoreOrderLineCustomField[]) => void;
  cancel: () => void;
}
//#endregion

export default function InstoreBasketPersoForm({
  fields,
  onSubmitHandler,
  cancel,
}: Props) {
  //#region SETUP
  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<{ items: InstoreOrderLineCustomField[] }>({
    mode: "all",
    defaultValues: { items: fields },
  });

  const { fields: rows, replace } = useFieldArray({ control, name: "items" });
  //#endregion

  //#region LOGIC
  useEffect(() => {
    replace(fields);
  }, [fields, replace]);

  const submitForm = (data: { items: InstoreOrderLineCustomField[] }) => {
    onSubmitHandler(data.items);
  };
  //#endregion

  //#region RENDER
  return (
    <main className="space-y-8">
      <header>
        <h2 className="text-base font-semibold leading-7 text-gray-900">
          Personalisation values
        </h2>
      </header>

      <form onSubmit={handleSubmit(submitForm)}>
        <ul>
          {rows.map((row, index) => (
            <li key={row.id} className="mt-4">
              <label className="block text-sm font-medium leading-6 mb-1">
                {row.fieldName}
              </label>

              <input
                className="block w-full rounded-md border p-2"
                {...register(`items.${index}.fieldValue` as const, {
                  required: `${row.fieldName} is required`,
                })}
                defaultValue={row.fieldValue}
              />

              {errors.items?.[index]?.fieldValue && (
                <p className="text-red-500 text-sm">
                  {errors.items[index]?.fieldValue?.message as string}
                </p>
              )}
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-between gap-x-6 mt-4">
          <button
            onClick={cancel}
            type="button"
            className="text-sm font-semibold text-pink-700 hover:text-pink-600"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={!isValid}
            className={classNames(
              isValid
                ? "bg-emerald-600 hover:bg-emerald-500"
                : "bg-gray-600 opacity-40",
              "rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm"
            )}
          >
            {isSubmitting ? "saving..." : "save items"}
          </button>
        </div>
      </form>
    </main>
  );
  //#endregion
}
